import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, DownloadSimple, TrendUp, TrendDown, Scales } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);
const blank = { kind: "expense", date: today(), amount: "", category: "Other", vendor: "",

                notes: "", business_percent: 100 };

export default function Expenses() {
  const [params] = useSearchParams();
  const [range, setRange] = useState({ start: daysAgo(90), end: today() });
  const [sum, setSum] = useState(null);
  const [txns, setTxns] = useState([]);
  const [cats, setCats] = useState({ expense: [], income: [] });
  const [open, setOpen] = useState(params.get("action") === "add");
  const [f, setF] = useState(blank);

  const load = useCallback(async () => {
    const [s, t] = await Promise.all([
      api.get("/finance/summary", { params: range }),
      api.get("/transactions", { params: range }),
    ]);
    setSum(s.data);
    setTxns(t.data);
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/finance/categories").then(({ data }) => setCats(data)); }, []);

  const save = async () => {
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter an amount");
    try {
      await api.post("/transactions", {
        ...f, amount: parseFloat(f.amount), business_percent: Number(f.business_percent),
      });
      toast.success(f.kind === "expense" ? "Expense recorded" : "Income recorded");
      setOpen(false);
      setF({ ...blank, kind: f.kind });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/transactions/${id}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const download = async () => {
    try {
      const { data } = await api.get(
        `/reports/profit-loss?start=${range.start}&end=${range.end}&format=csv`, { responseType: "blob" });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `profit_and_loss_${range.start}_to_${range.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success("Profit & loss downloaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const options = f.kind === "expense" ? cats.expense : cats.income;
  const top = sum ? Object.entries(sum.expenses_by_category) : [];
  const max = top.length ? top[0][1] : 1;

  return (
    <div data-testid="expenses-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Business</p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Money in, money out</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={download} className="rounded-full h-12 px-6 font-bold"
                  data-testid="pl-download-btn"><DownloadSimple size={17} className="mr-2" /> Profit & loss</Button>
          <Button onClick={() => { setF({ ...blank, kind: "expense" }); setOpen(true); }}
                  className="rounded-full h-12 px-6 bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="add-transaction-btn"><Plus size={18} className="mr-2" /> Add entry</Button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-4 items-end">
        <div>
          <Label className="text-sm font-bold">From</Label>
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })}
                 className="mt-2 h-11 rounded-xl w-44" data-testid="finance-start" />
        </div>
        <div>
          <Label className="text-sm font-bold">To</Label>
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })}
                 className="mt-2 h-11 rounded-xl w-44" data-testid="finance-end" />
        </div>
      </div>

      {!sum ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="finance-income">
              <TrendUp size={22} weight="duotone" className="text-[#10B981]" />
              <p className="mt-4 font-display text-3xl font-bold">{money(sum.total_income)}</p>
              <p className="mt-1 text-sm text-muted-foreground font-semibold">Total income</p>
              <p className="mt-2 text-xs text-muted-foreground">
                {money(sum.tuition_collected)} tuition · {money(sum.other_income)} other
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="finance-expenses">
              <TrendDown size={22} weight="duotone" className="text-[#EF4444]" />
              <p className="mt-4 font-display text-3xl font-bold">{money(sum.total_expenses)}</p>
              <p className="mt-1 text-sm text-muted-foreground font-semibold">Total expenses</p>
              <p className="mt-2 text-xs text-muted-foreground">{sum.transaction_count} entries logged</p>
            </div>
            <div className={`rounded-2xl border-2 p-6 ${sum.net_profit >= 0
              ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="finance-profit">
              <Scales size={22} weight="duotone" className={sum.net_profit >= 0 ? "text-[#10B981]" : "text-[#EF4444]"} />
              <p className={`mt-4 font-display text-3xl font-bold ${sum.net_profit >= 0 ? "text-[#059669]" : "text-[#DC2626]"}`}>
                {money(sum.net_profit)}
              </p>
              <p className="mt-1 text-sm font-bold">{sum.net_profit >= 0 ? "Profit" : "Loss"}</p>
            </div>
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="finance-margin">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Margin</p>
              <p className="mt-4 font-display text-3xl font-bold">{sum.margin_percent}%</p>
              <p className="mt-1 text-sm text-muted-foreground font-semibold">of income kept</p>
            </div>
          </div>

          <h2 className="mt-10 font-display text-2xl font-semibold">Where the money went</h2>
          <div className="mt-4 bg-white rounded-2xl border border-border p-6" data-testid="category-breakdown">
            {top.length === 0 ? (
              <p className="text-muted-foreground">No expenses logged in this range yet.</p>
            ) : (
              <div className="space-y-4">
                {top.map(([cat, amt]) => (
                  <div key={cat} className="flex items-center gap-4" data-testid={`category-${cat}`}>
                    <span className="text-sm font-bold w-44 truncate">{cat}</span>
                    <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-[#3B82F6]" style={{ width: `${(amt / max) * 100}%` }} />
                    </div>
                    <span className="text-sm font-bold w-24 text-right">{money(amt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <h2 className="mt-10 font-display text-2xl font-semibold">Entries</h2>
          <div className="mt-4 space-y-3" data-testid="transactions-list">
            {txns.length === 0 && <p className="text-muted-foreground">Nothing logged in this range.</p>}
            {txns.map((t) => (
              <div key={t.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                   data-testid={`transaction-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold">
                    {t.vendor || t.category}
                    <span className={`ml-3 text-xs font-bold px-2.5 py-1 rounded-full ${
                      t.kind === "income" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                      {t.kind}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 font-semibold">
                    {t.date} · {t.category}
                    {t.business_percent < 100 ? ` · ${t.business_percent}% business` : ""}
                    {t.notes ? ` · ${t.notes}` : ""}
                  </p>
                </div>
                <p className="font-display text-xl font-bold">
                  {money(t.business_amount ?? t.amount)}
                </p>
                <Button size="sm" variant="outline" onClick={() => del(t.id)}
                        className="rounded-full font-bold text-[#EF4444]" data-testid={`delete-transaction-${t.id}`}>
                  <Trash size={15} />
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl" data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add an entry</DialogTitle>
            <DialogDescription className="sr-only">Record an expense or a source of income.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-3">
              {["expense", "income"].map((k) => (
                <Button key={k} onClick={() => setF({ ...f, kind: k, category: "Other" })}
                        className={`flex-1 h-11 rounded-full font-bold capitalize ${f.kind === k
                          ? "bg-[#3B82F6] hover:bg-[#2563EB]"
                          : "bg-muted text-foreground hover:bg-slate-200"}`}
                        data-testid={`txn-kind-${k}`}>{k}</Button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold">Date</Label>
                <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="txn-date" />
              </div>
              <div>
                <Label className="text-sm font-bold">Amount (USD)</Label>
                <Input type="number" step="0.01" value={f.amount}
                       onChange={(e) => setF({ ...f, amount: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="txn-amount" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="txn-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold">Paid to / received from</Label>
              <Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })}
                     placeholder="Sam's Club, Entergy…" className="mt-2 h-11 rounded-xl" data-testid="txn-vendor" />
            </div>
            {f.kind === "expense" && (
              <div>
                <Label className="text-sm font-bold">Business portion: {f.business_percent}%</Label>
                <Input type="range" min="0" max="100" step="5" value={f.business_percent}
                       onChange={(e) => setF({ ...f, business_percent: Number(e.target.value) })}
                       className="mt-2" data-testid="txn-business-percent" />
                <p className="mt-1 text-xs text-muted-foreground">
                  For shared costs like a home utility bill, only the business share is counted.
                </p>
              </div>
            )}
            <div>
              <Label className="text-sm font-bold">Notes</Label>
              <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                        className="mt-2 rounded-xl" data-testid="txn-notes" />
            </div>
            <Button onClick={save} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="txn-save-btn">Save entry</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
